import { baseApi } from "../../../services/rtkApi/baseApi";
import { emojiPaths as P, emojiAdminPaths as A } from "./emojiPaths";
import type {
  AdminEntryResponse,
  AdminListFilter,
  AdminListResponse,
  AdminStatsResponse,
  CandidatesResponse,
  ClaimResponse,
  CreateEmojiRequest,
  ReserveEmojiRequest,
  SelectorResponse,
  SyncReservationsResponse,
  UpdateEmojiRequest,
} from "../types/emojiTypes";

export const emojiApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** The full catalog + per-user state. Powers <EmojiSelector> + <EmojiKeyboard>. */
    getEmojiSelector: builder.query<SelectorResponse, void>({
      query: () => P.selector,
      providesTags: ["EmojiSelector"],
    }),

    /** Claim your permanent, unique signature emoji. */
    claimEmoji: builder.mutation<ClaimResponse, { emoji: string }>({
      query: (body) => ({ url: P.claim, method: "POST", body }),
      invalidatesTags: ["EmojiSelector", "EmojiRegistry"],
    }),

    /* ---- admin ---- */
    adminListEmojis: builder.query<AdminListResponse, AdminListFilter | void>({
      query: (arg) => ({
        url: A.registry,
        params: {
          ...(arg?.status ? { status: arg.status } : {}),
          ...(arg?.state ? { state: arg.state } : {}),
          ...(arg?.category ? { category: arg.category } : {}),
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.reservedKind ? { reservedKind: arg.reservedKind } : {}),
          ...(arg?.limit !== undefined ? { limit: arg.limit } : {}),
          ...(arg?.offset !== undefined ? { offset: arg.offset } : {}),
        },
      }),
      providesTags: ["EmojiRegistry"],
    }),

    adminEmojiStats: builder.query<AdminStatsResponse, void>({
      query: () => A.stats,
      providesTags: ["EmojiRegistry"],
    }),

    adminCreateEmoji: builder.mutation<AdminEntryResponse, CreateEmojiRequest>({
      query: (body) => ({ url: A.registry, method: "POST", body }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminUpdateEmoji: builder.mutation<AdminEntryResponse, { id: string; body: UpdateEmojiRequest }>({
      query: ({ id, body }) => ({ url: A.registryItem(id), method: "PATCH", body }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminDeleteEmoji: builder.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({ url: A.registryItem(id), method: "DELETE" }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminReserveEmoji: builder.mutation<AdminEntryResponse, { id: string; body: ReserveEmojiRequest }>({
      query: ({ id, body }) => ({ url: A.registryReserve(id), method: "POST", body }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminReleaseEmoji: builder.mutation<AdminEntryResponse, { id: string; force?: boolean }>({
      query: ({ id, force }) => ({
        url: A.registryRelease(id),
        method: "POST",
        body: { force: force === true },
      }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminSyncReservations: builder.mutation<SyncReservationsResponse, void>({
      query: () => ({ url: A.reservationsSync, method: "POST" }),
      invalidatesTags: ["EmojiRegistry", "EmojiSelector"],
    }),

    adminListCandidates: builder.query<CandidatesResponse, void>({
      query: () => A.candidates,
      providesTags: ["EmojiCandidates"],
    }),

    adminPromoteCandidate: builder.mutation<
      AdminEntryResponse,
      { emoji: string; category: string; name?: string | null }
    >({
      query: (body) => ({ url: A.candidatesPromote, method: "POST", body }),
      invalidatesTags: ["EmojiCandidates", "EmojiRegistry", "EmojiSelector"],
    }),

    adminDismissCandidate: builder.mutation<{ success: boolean }, { emoji: string }>({
      query: (body) => ({ url: A.candidatesDismiss, method: "POST", body }),
      invalidatesTags: ["EmojiCandidates"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmojiSelectorQuery,
  useClaimEmojiMutation,
  useAdminListEmojisQuery,
  useAdminEmojiStatsQuery,
  useAdminCreateEmojiMutation,
  useAdminUpdateEmojiMutation,
  useAdminDeleteEmojiMutation,
  useAdminReserveEmojiMutation,
  useAdminReleaseEmojiMutation,
  useAdminSyncReservationsMutation,
  useAdminListCandidatesQuery,
  useAdminPromoteCandidateMutation,
  useAdminDismissCandidateMutation,
} = emojiApi;
